<script setup>
import {computed} from "vue";
import {WirtzWorldPhone, WirtzWorldUrl} from "@/constants";
import {useApartmentStore} from "@/stores/apartment";
import AppIcon from "@/components/AppIcon.vue";

const apartmentStore = useApartmentStore()

const websiteLabel = computed(() => (WirtzWorldUrl ?? '').replace(/^https?:\/\//, '').replace(/\/$/, ''))
const location = computed(() => apartmentStore.apartment.location ?? {})

const addressLines = computed(() => {
  const {street, address, postalCode, city} = location.value
  const line1 = street ?? address
  const line2 = [postalCode, city].filter(Boolean).join(' ')

  return [line1, line2].filter(Boolean)
})

const mapsUrl = computed(() => {
  if (!addressLines.value.length) {
    return null
  }

  return 'https://maps.google.com/?q=' + encodeURIComponent(addressLines.value.join(', '))
})
</script>

<template>
  <div class="ww-screen">
    <h1 class="ww-title">{{ $t('contact.title') }}</h1>
    <p class="ww-subtitle">{{ $t('contact.subtitle') }}</p>

    <div class="contact__rows">
      <a :href="'https://wa.me/' + WirtzWorldPhone" target="_blank" rel="noopener" class="ww-row">
        <span class="ww-plate contact__plate--whatsapp">
          <svg viewBox="0 0 24 24" width="23" height="23" fill="#25D366" aria-hidden="true">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.347-.347.52-.52.174-.174.232-.298.347-.497.116-.198.058-.371-.031-.52-.087-.148-.662-1.595-.906-2.19-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L0 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"></path>
          </svg>
        </span>
        <span class="ww-row__body">
          <span class="ww-row__title">WhatsApp</span>
          <span class="ww-row__sub">{{ $t('contact.whatsappSub') }}</span>
        </span>
        <app-icon name="chevron_right" :size="21" class="ww-row__chevron"/>
      </a>

      <a :href="'tel:' + WirtzWorldPhone" class="ww-row">
        <span class="ww-plate">
          <app-icon name="call" :size="22"/>
        </span>
        <span class="ww-row__body">
          <span class="ww-row__title">{{ $t('phone') }}</span>
          <span class="ww-row__sub">{{ WirtzWorldPhone }}</span>
        </span>
        <app-icon name="chevron_right" :size="21" class="ww-row__chevron"/>
      </a>

      <a :href="WirtzWorldUrl" target="_blank" rel="noopener" class="ww-row">
        <span class="ww-plate">
          <app-icon name="language" :size="22"/>
        </span>
        <span class="ww-row__body">
          <span class="ww-row__title">{{ $t('website') }}</span>
          <span class="ww-row__sub">{{ websiteLabel }}</span>
        </span>
        <app-icon name="chevron_right" :size="21" class="ww-row__chevron"/>
      </a>
    </div>

    <section class="ww-card ww-card--navy contact__address">
      <div class="ww-eyebrow ww-eyebrow--onNavy">{{ $t('contact.addressTitle') }}</div>
      <div class="contact__address-value">Apartment {{ apartmentStore.apartment.name }}</div>
      <div v-if="addressLines.length" class="contact__address-lines">
        <div v-for="(line, i) in addressLines" :key="'addr-' + i">{{ line }}</div>
      </div>
      <div v-else-if="location.floor !== undefined" class="contact__address-lines">
        {{ location.floor }}. {{ $t('floor') }}, {{ $t(location.side) }}
      </div>

      <a v-if="mapsUrl" :href="mapsUrl" target="_blank" rel="noopener" class="contact__maps">
        <app-icon name="map" :size="19"/>
        {{ $t('contact.mapsLink') }}
      </a>
    </section>
  </div>
</template>

<style scoped>
.contact__rows {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 20px;
}

.contact__rows .ww-row__title,
.contact__rows .ww-row__sub {
  display: block;
}

.contact__plate--whatsapp {
  background: #eafaf0;
}

.contact__address {
  margin-top: 22px;
  border-radius: var(--ww-r-lg);
  padding: 20px;
}

.contact__address-value {
  margin-top: 8px;
  font-size: 17px;
  font-weight: 700;
  line-height: 1.35;
}

.contact__address-lines {
  margin-top: 4px;
  font-size: 15px;
  font-weight: 500;
  line-height: 1.4;
  color: var(--ww-on-navy);
}

.contact__maps {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  margin-top: 14px;
  font-size: 14px;
  font-weight: 700;
  color: var(--ww-primary-bright);
  text-decoration: none;
}

.contact__maps:hover {
  color: #8fd6f0;
}
</style>
